<template>
	<div class="loan-list" data-tour="cc-contact-loan-list">
		<div class="loan-list-controls" v-if="showButtons">
			<button
				class="b360-btn--primary"
				@click="handleLoanClick()"
				data-test="add-loan"
			>
				Add Loan
			</button>
		</div>
		<LoadingIcon v-if="isLoading" alignment="center" />
		<FallBack
			v-else-if="!isLoading && error"
			iconStyle="fal"
			icon="exclamation-triangle"
			:border="false"
			message="Unable to retrieve loans. Please try again later."
		/>
		<FallBack
			v-else-if="!isLoading && !error && isEmpty(loans)"
			iconStyle="far"
			icon="home-lg-alt"
			:border="false"
			message="No loans to view."
		/>
		<div v-else-if="!isLoading && !error && !isEmpty(loans)">
			<div
				class="loan-container"
				data-test="loan-container"
				:data-tour="index === 0 ? 'cc-contact-loan' : ''"
				v-for="(loan, index) in currentPageLoans"
				:key="index"
				@click="handleLoanClick(loan.loanId)"
			>
				<AlertTypeIcon alertType="Loan Listing" />
				<div class="loan-open" role="button" aria-label="Open loan">
					<LoanInfo :loan="loan" />
				</div>
				<div @click.stop>
					<QuickActionsMenu
						v-if="showButtons"
						:data-tour="
							index === 0 ? 'cc-contact-loan-quick-actions' : ''
						"
						:lightButton="true"
						:lightButtonIcon="['far', 'ellipsis-h']"
						:menus="populateQuickActions(loan.uwmInd)"
						@editLoan="handleLoanClick(loan.loanId)"
						@deleteLoan="handleDeleteLoan(loan.loanId)"
						@sendToBlink="
							$emit(
								'sendToBlinkClick',
								loan.loanId,
								loan.subjectPropertyAddress.addressLine1,
							)
						"
						@viewInEase="handleEaseLinkClick(loan.easeLoanUrl)"
					/>
				</div>
			</div>
			<Pagination
				v-if="totalCount > 10 && !isLoading"
				:totalCount="totalCount"
				:pageNumber="pageNumber"
				:pageSize="pageSize"
				@onPageChange="handlePageNumberChange"
				@onPageSizeChange="handlePageSizeChange"
			/>
		</div>
	</div>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapState } from 'vuex';

import AlertTypeIcon from '../AlertTypeIcon.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import Pagination from '../../../components/global/Pagination.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import LoanInfo from './LoanInfo.vue';
import FallBack from '../../../components/global/FallBack.vue';

export default {
	name: 'LoansList',
	components: {
		AlertTypeIcon,
		LoadingIcon,
		Pagination,
		QuickActionsMenu,
		LoanInfo,
		FallBack,
	},
	props: {
		leadId: Number,
		contactId: Number,
		blinkDisabled: {
			type: Boolean,
			default: null,
		},
		loggedInUserId: {
			type: Number,
			default: null,
		},
		view: {
			type: String,
			default: 'loans',
			validator(value) {
				return value === 'loans' || value === 'settings';
			},
		},
	},
	data() {
		return {
			pageNumber: 1,
			pageSize: 10,
			totalCount: null,
			isLoading: false,
			error: null,
			currentPageLoans: [],
			slicedLoans: [],
		};
	},
	computed: {
		...mapState({
			loans: (state) => state.ccContacts.loans.listWithDetails,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		showButtons() {
			return this.view === 'loans';
		},
	},
	methods: {
		...mapActions(['getLeadLoansForId', 'setLoanDetailsView']),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		handleDeleteLoan(loanId) {
			this.$emit('onDeleteLoanClick', loanId);
		},
		populateQuickActions(isUWM) {
			return [
				[
					{
						icon: ['far', 'pen'],
						text: 'Edit Loan',
						action: 'editLoan',
					},
					{
						icon: ['far', 'file-import'],
						text: 'Send to Blink+',
						action: 'sendToBlink',
						disabled:
							this.blinkDisabled ||
							this.mainProfileIsInternalUser ||
							this.loggedInUserId !== this.contactId,
					},
					{
						icon: ['far', 'external-link-alt'],
						text: 'View in EASE',
						action: 'viewInEase',
						disabled: !isUWM,
					},
				],
				[
					{
						icon: ['far', 'trash-alt'],
						text: 'Delete Loan',
						action: 'deleteLoan',
						iconColor: 'red',
						disabled: this.mainProfileIsInternalUser,
					},
				],
			];
		},
		handleLoanClick(id = null) {
			const detailsView =
				this.view === 'settings' ? 'Settings' : 'Details';
			this.$emit('onLoanClick', id, detailsView);
		},
		handleEaseLinkClick(url) {
			window.open(url, '_blank');
		},
		async getLeadLoans() {
			const { leadId } = this;
			try {
				await this.getLeadLoansForId({ leadId });
			} catch (error) {
				this.error = error;
			}
		},
		getCurrentPageLoans() {
			this.slicedLoans =
				this.loans &&
				this.loans.slice(
					(this.pageNumber - 1) * this.pageSize,
					this.pageNumber * this.pageSize,
				);
			if (!isEmpty(this.slicedLoans)) {
				this.currentPageLoans = [...this.slicedLoans];
			}
		},
		handlePageNumberChange(page) {
			this.pageNumber = page;
			this.getCurrentPageLoans();
		},
		handlePageSizeChange(size) {
			this.pageSize = size;
			this.pageNumber = 1;
			this.getCurrentPageLoans();
		},
	},
	async created() {
		this.isLoading = true;
		await this.getLeadLoans();
		this.totalCount = this.loans && this.loans.length;
		this.getCurrentPageLoans();
		this.isLoading = false;
		this.setLoanDetailsView('Details');
	},
	watch: {
		async pageNumber() {
			this.getCurrentPageLoans();
		},
		async slicedLoans(value) {
			if (this.error) {
				return;
			}
			const lastPage = this.totalCount / this.pageSize;
			if (this.pageNumber === lastPage && !isEmpty(value)) {
				// eslint-disable-next-line no-useless-return
				return;
				// eslint-disable-next-line no-else-return
			} else if (
				(isEmpty(value) || value.length !== this.pageSize) &&
				this.totalCount > this.loans.length &&
				!this.error
			) {
				this.isLoading = true;
				await this.getLeadLoans();
				this.getCurrentPageLoans();
				this.isLoading = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.loan-list {
	@include rem('padding-bottom', 6px);
}

.loan-list-controls {
	display: flex;
	justify-content: flex-end;
	@include rem('padding', 17px 20px);
	border-bottom: 1px solid $border-color-light;
}

.spinner {
	@include rem('padding', 25px);
	display: flex;
	justify-content: center;
}

.loan-container {
	cursor: pointer;
	display: grid;
	grid-template-columns: 46px auto 34px;
	@include rem('padding', 17px 20px);
	@include rem('grid-gap', 15px);
	background: $white;

	&:not(:last-child) {
		border-bottom: 1px solid $border-color-light;
	}

	& > *:last-child {
		align-self: center;
	}

	/deep/ .btn-quick-actions {
		@include rem('height', 34px);
		@include rem('width', 34px);
	}
}

.loan-open {
	cursor: pointer;
}
</style>
